import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil } from 'lucide-react';
import { PortalDsButton } from 'src/components/portal-design-system/portal-ds-button';
import { portalDs } from 'src/components/portal-design-system/portal-ds-classes';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { cn } from 'src/lib/utils';
import PacingBoardStatusBadge from './pacing-board-status-badge';
import type { PacingBudget } from '../../types/pacing-board.types';

type SortKey = 'campaign_name' | 'status' | 'amount_per_day' | 'net_budget';
type SortDir = 'asc' | 'desc';

function formatCurrency(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(num || 0);
}

function formatAmountPerDay(value: number | string | null | undefined): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num || 0);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateRange(start: string, end: string): string {
  return `${formatDate(start)} – ${formatDate(end)}`;
}

function spendPercent(budget: PacingBudget): number {
  const total = parseFloat(budget.net_budget);
  const actual = budget.detail?.actual_spend ?? 0;
  if (!total) return 0;
  return Math.round((actual / total) * 100);
}

/** Percent of flight elapsed (0–100), based on elapsed_days / total_days. */
function timePercent(budget: PacingBudget): number | null {
  const totalDays = budget.detail?.sparkline?.total_days;
  const elapsed = budget.detail?.elapsed_days;
  if (totalDays == null || totalDays <= 0 || elapsed == null) return null;
  return Math.min(100, Math.round((elapsed / totalDays) * 100));
}

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-50" />;
  return dir === 'asc'
    ? <ArrowUp className="ml-1 inline h-3 w-3" />
    : <ArrowDown className="ml-1 inline h-3 w-3" />;
}

interface PacingCampaignsTableProps {
  budgets: PacingBudget[];
  canEdit: boolean;
  isRefreshing?: boolean;
  onEdit: (budgetId: string) => void;
  onAddBudget?: () => void;
}

export default function PacingCampaignsTable({
  budgets,
  canEdit,
  isRefreshing = false,
  onEdit,
  onAddBudget,
}: PacingCampaignsTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('campaign_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sortedBudgets = useMemo(() => {
    const next = [...budgets];
    next.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'campaign_name':
          cmp = a.campaign_name.localeCompare(b.campaign_name);
          break;
        case 'status':
          cmp = (a.detail?.status ?? '').localeCompare(b.detail?.status ?? '');
          break;
        case 'amount_per_day':
          cmp = (a.detail?.amount_per_day ?? 0) - (b.detail?.amount_per_day ?? 0);
          break;
        case 'net_budget':
          cmp = parseFloat(a.net_budget) - parseFloat(b.net_budget);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return next;
  }, [budgets, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortableHead = (key: SortKey, label: string, className?: string) => (
    <TableHead
      className={cn(portalDs.tableHeadCell, portalDs.tableHeadCellSortable, className)}
      onClick={() => handleSort(key)}
      aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      <SortIcon active={sortKey === key} dir={sortDir} />
    </TableHead>
  );

  if (budgets.length === 0 && !isRefreshing) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-[#F4F5F8] p-8">
        <p className="text-sm text-[#5E6278]">No campaigns on this board yet.</p>
        {canEdit && onAddBudget && (
          <PortalDsButton variant="accent" size="sm" onClick={onAddBudget}>
            Add campaign
          </PortalDsButton>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden bg-[#F4F5F8] p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h2 className={portalDs.sectionTitle}>Campaigns</h2>
          <p className={portalDs.sectionSubtitle}>
            {budgets.length} campaign{budgets.length === 1 ? '' : 's'}
            {isRefreshing && ' · Refreshing…'}
          </p>
        </div>
        {canEdit && onAddBudget && (
          <PortalDsButton variant="secondary" size="sm" onClick={onAddBudget}>
            Add campaign
          </PortalDsButton>
        )}
      </div>

      <div className={portalDs.tableContainer}>
        <Table>
          <TableHeader>
            <TableRow className={portalDs.tableHead}>
              {sortableHead('status', 'Status')}
              {sortableHead('campaign_name', 'Campaign')}
              <TableHead className={portalDs.tableHeadCell}>Dates</TableHead>
              {sortableHead('net_budget', 'Budget', 'text-right')}
              <TableHead className={cn(portalDs.tableHeadCell, 'text-right')}>Actual</TableHead>
              <TableHead className={cn(portalDs.tableHeadCell, 'text-right')}>% Spent / Time</TableHead>
              {sortableHead('amount_per_day', 'Amt per day', 'text-right')}
              {canEdit && (
                <TableHead className={cn(portalDs.tableHeadCell, 'w-10')} aria-label="Actions" />
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedBudgets.map((budget) => {
              const detail = budget.detail;
              const elapsedPct = timePercent(budget);

              return (
                <TableRow key={budget.id} className={cn(portalDs.tableBodyRow, 'group')}>
                  <TableCell className={portalDs.tableCell}>
                    <PacingBoardStatusBadge status={detail?.status} />
                  </TableCell>
                  <TableCell className={cn(portalDs.tableCell, 'font-semibold')}>
                    {budget.campaign_name}
                  </TableCell>
                  <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                    {formatDateRange(budget.start_date, budget.end_date)}
                  </TableCell>
                  <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMono, 'text-right')}>
                    <div>{formatCurrency(budget.gross_budget)}</div>
                    <div className="text-xs text-[#9CA3AF]">
                      {parseFloat(budget.margin_pct).toFixed(1)}% margin · net {formatCurrency(budget.net_budget)}
                    </div>
                  </TableCell>
                  <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMono, 'text-right')}>
                    {formatCurrency(detail?.actual_spend)}
                  </TableCell>
                  <TableCell
                    className={cn(portalDs.tableCell, portalDs.tableCellMono, 'text-right')}
                    title="Percent spent / percent of flight time elapsed"
                  >
                    {elapsedPct != null
                      ? `${spendPercent(budget)} / ${elapsedPct}`
                      : `${spendPercent(budget)} / —`}
                  </TableCell>
                  <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMono, 'text-right')}>
                    {detail ? formatAmountPerDay(detail.amount_per_day) : '—'}
                  </TableCell>
                  {canEdit && (
                    <TableCell className={portalDs.tableCell}>
                      <div className={portalDs.tableRowActions}>
                        <PortalDsButton
                          variant="ghost"
                          size="sm"
                          className="!px-1.5 !py-1.5"
                          onClick={() => onEdit(budget.id)}
                          aria-label={`Edit ${budget.campaign_name}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </PortalDsButton>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
